import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import {
  NOTIFICATION_ALL_ALREADY_READ_MESSAGE,
  NOTIFICATION_MARK_READ_CONFLICT_MESSAGE,
} from '@/constants/messages';
import { useAuth } from '@/shared/providers/AuthContext';
import {
  fetchClinicNotificationList,
  fetchClinicNotificationUnreadCounts,
  markClinicNotificationsRead,
  retryClinicNotifications,
} from '@/services/main/notificationCenter';
import type {
  NotificationBulkResult,
  NotificationListQuery,
} from '@/types/admin-notification-center';

/**
 * 通知センター for the 治療院 portal.
 *
 * A SEPARATE KEY SPACE from the admin screen's (`notification-center`). The two lists answer for different
 * scopes, and one cache key would let an admin's page invalidate — or worse, serve — a clinic's rows.
 */
const LIST_KEY = 'clinic-notification-center';
const COUNT_KEY = 'clinic-notification-center-unread';

/**
 * The tenant + clinic in the URL.
 *
 * NOT the scope. The server takes clinic_id off the authenticated user; these two only build the path, the
 * same way every other clinic-portal service does.
 *
 * READS AUTH DIRECTLY INSTEAD OF useClinicContext, which throws when `clinic` is not set yet. That throw was
 * harmless while only the 通知センター page used these hooks — that page mounts after auth resolves. It stopped
 * being harmless when TenantHeader started calling the count hook for its bell: the header renders on every
 * clinic-portal screen, including the window where `user` has arrived and `clinic` has not, and a hook that
 * throws during a header render takes the whole portal down with it. Missing values fall through to the
 * `enabled` guard below, which is what the `?? ''` here was always written for.
 */
const useClinicPath = () => {
  const { clinic } = useAuth();
  return { tenant: clinic?.site_type ?? '', clinicNumber: clinic?.clinic_number ?? '' };
};

export const useClinicNotificationList = (query: NotificationListQuery) => {
  const { tenant, clinicNumber } = useClinicPath();

  return useQuery({
    queryKey: [LIST_KEY, tenant, clinicNumber, query],
    queryFn: () => fetchClinicNotificationList(tenant, clinicNumber, query),
    // The list is a feed: a stale page that still offers 再送信 on a row someone else already fixed is worse
    // than a refetch. Kept short rather than zero so paging back and forth is not a round trip each way.
    staleTime: 10_000,
    placeholderData: previous => previous,
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * The number the header bell wears. Runs on EVERY clinic-portal screen, not just this one.
 *
 * `retry: false` because of that reach: a 401 here goes through the same interceptor that redirects to the
 * login screen, and three retries would be three redirect attempts for one dead session. One is enough, and
 * a badge is never worth insisting on.
 */
export const useClinicNotificationUnreadCounts = () => {
  const { tenant, clinicNumber } = useClinicPath();

  return useQuery({
    queryKey: [COUNT_KEY, tenant, clinicNumber],
    queryFn: () => fetchClinicNotificationUnreadCounts(tenant, clinicNumber),
    staleTime: 10_000,
    retry: false,
    enabled: !!tenant && !!clinicNumber,
  });
};

type BulkAction = 'markRead' | 'retry';

const RUNNERS: Record<
  BulkAction,
  (tenant: string, clinicNumber: string, ids: number[]) => Promise<NotificationBulkResult>
> = {
  markRead: markClinicNotificationsRead,
  retry: retryClinicNotifications,
};

/**
 * Both bulk buttons go through here, so they report the same way.
 *
 * The toast quotes what ACTUALLY happened, not what was asked for: 「20件中 14件を再送信しました
 * (6件は対象外)」. A plain 「送信しました」 over a mixed selection would be a quiet lie about six rows.
 */
export const useClinicNotificationBulkAction = () => {
  const { tenant, clinicNumber } = useClinicPath();
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      action,
      ids,
    }: {
      action: BulkAction;
      ids: number[];
      /**
       * How many of the ticked rows the LIST DREW as 未読. Not sent to the server — it is what the reader
       * believed, and it is the only way to tell a stale list from a deliberate tick of an 既読 row once the
       * bulk endpoint has answered 「nothing moved」. See onSuccess.
       */
      expectedUnread?: number;
    }) => RUNNERS[action](tenant, clinicNumber, ids),
    onSuccess: async (result, { action, expectedUnread = 0 }) => {
      // Named for what the remainder IS, per action. 「対象外」 on a mark-read reads as a problem; those rows
      // were simply already read, which is the normal case when someone ticks a whole page.
      const tail =
        result.skipped > 0
          ? action === 'markRead'
            ? `（${result.skipped}件は既に既読）`
            : `（${result.skipped}件は対象外）`
          : '';

      if (result.affected === 0) {
        if (action === 'markRead') {
          /**
           * NOTHING MOVED — and the reason decides the sentence.
           *
           * The endpoint is bulk and answers 200 with `affected` / `skipped`, so unlike the admin screen's
           * per-id 409 the server cannot tell these two apart: `skipped` counts 「was already 既読」 without
           * saying WHEN it became so. What separates them is what the reader was looking at, which only the
           * screen knows — hence `expectedUnread`, the number of ticked rows the list drew as 未読.
           *
           *   expectedUnread === 0  the reader ticked rows already showing 既読. Ordinary; nothing is stale.
           *   expectedUnread > 0    a row drawn as 未読 moved nowhere, so something else read it in between
           *                         (the RAH admin screen, or a second tab). The list on screen is out of
           *                         date and every other row on it may be too — hence 「reload」.
           */
          toast.warning(
            expectedUnread > 0
              ? NOTIFICATION_MARK_READ_CONFLICT_MESSAGE
              : NOTIFICATION_ALL_ALREADY_READ_MESSAGE
          );
        } else {
          toast.warning(`対象の通知がありませんでした。${tail}`);
        }
      } else if (action === 'markRead') {
        toast.success(`${result.affected}件を既読にしました。${tail}`);
      } else {
        toast.success(`${result.affected}件を再送信しました。${tail}`);

        // The 500-per-press cap, said out loud. See the admin hook for why the success count alone is not
        // enough: it counts ROWS acted on, while the leftover is counted in REZEPTS.
        const remaining = result.remaining ?? 0;
        if (remaining > 0) {
          toast.info(
            `未同期のレセプトが${remaining}件残っています。もう一度実行してください。`
          );
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
        queryClient.invalidateQueries({ queryKey: [COUNT_KEY] }),
      ]);
    },
    onError: error => {
      if ((error as { response?: { status?: number } })?.response?.status === 401) return;
      toast.error('エラーが発生しました。');
    },
  });
};
